import type { MouseEvent, ReactElement, ReactNode } from 'react';
import { ActionIcon, Badge, Group, Table, Text } from '@mantine/core';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { MegaGridColumn } from './MegaGridTypes';

interface MegaGridGroupRowProps<T> {
    label: string;
    count: number;
    depth: number;
    collapsed: boolean;
    colSpan: number;
    onToggle: () => void;
    /** When set, clicking the row selects the group (e.g. to filter) while the chevron still toggles expansion. */
    onSelect?: () => void;
    summary?: ReactNode;
    /** Visible columns; when any defines `groupRender`, the row renders aligned per-column cells. */
    columns?: MegaGridColumn<T>[];
    /** The group's leaf rows, passed to each column's `groupRender`. */
    rows?: T[];
}

/**
 * A collapsible group header row — spanning all columns with a right-aligned
 * summary, or aligned per-column cells when columns define `groupRender`. The
 * whole row is clickable (toggles expansion, or selects when `onSelect` is set);
 * the chevron always toggles expansion.
 */
export default function MegaGridGroupRow<T>({ label, count, depth, collapsed, colSpan, onToggle, onSelect, summary, columns, rows }: MegaGridGroupRowProps<T>): ReactElement {
    const aligned = columns !== undefined && rows !== undefined && columns.some((c) => c.groupRender !== undefined);
    const onRowClick = onSelect ?? onToggle;
    const onChevron = (event: MouseEvent<HTMLButtonElement>): void => {
        event.stopPropagation();
        onToggle();
    };

    const groupLabel = (
        <Group gap={6} wrap="nowrap" style={{ minWidth: 0, paddingLeft: depth * 18 }}>
            <ActionIcon variant="subtle" color="gray" size="sm" onClick={onChevron} aria-label={collapsed ? `Expand ${label}` : `Collapse ${label}`}>
                {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
            </ActionIcon>
            <Text size="xs" fw={700} truncate>
                {label}
            </Text>
            <Badge size="xs" variant="light" color="gray" radius="sm">
                {count}
            </Badge>
        </Group>
    );

    if (aligned && columns !== undefined && rows !== undefined) {
        return (
            <Table.Tr style={{ background: 'var(--bm-color-surface-alt)', cursor: 'pointer' }} onClick={onRowClick}>
                {columns.map((col, index) => (
                    <Table.Td key={col.key} style={{ textAlign: col.align ?? 'left', paddingTop: 5, paddingBottom: 5 }}>
                        {index === 0 ? groupLabel : (col.groupRender?.(rows) ?? null)}
                    </Table.Td>
                ))}
            </Table.Tr>
        );
    }

    return (
        <Table.Tr style={{ background: 'var(--bm-color-surface-alt)', cursor: 'pointer' }} onClick={onRowClick}>
            <Table.Td colSpan={colSpan} style={{ padding: `4px 12px` }}>
                <Group gap={6} justify="space-between" wrap="nowrap">
                    {groupLabel}
                    {summary}
                </Group>
            </Table.Td>
        </Table.Tr>
    );
}
